import React from 'react';
import { GoPlus } from 'react-icons/go';
import { SlFlag } from 'react-icons/sl';
import { IoIosArrowDown } from 'react-icons/io';
import { LuUsers } from 'react-icons/lu';
import { AiOutlineZoomIn, AiOutlineZoomOut } from 'react-icons/ai';
import FlowNodeCard from './FlowNodeCard';
import NodeAnalyticsWidget from './NodeAnalyticsWidget';
import {
  CanvasArea,
  CanvasTransformContent,
  StartNode,
  ConnectorLine,
  ConnectorLineSimple,
  FlowNodeCard as UploadNodeCard,
  FlowNodeHeader,
  FlowNodeIcon,
  FlowNodeTitle,
  FlowNodeBody,
  SelectDropdown,
  AddNodeButton,
  BranchContainer,
  BranchSplit,
  BranchArm,
  BranchLabel,
  BranchVerticalLine,
  BranchSplitFour,
  BranchArmFour,
  EndNodeCard,
  EndNodeHeader,
  EndNodeBody,
  StatusTable,
  ZoomPanel,
  ZoomButton,
  ZoomTrack,
  ZoomIndicatorDynamic,
  LeadListText,
  NodeRow,
} from './FlowBuilder.styles';

const BRANCHING_TYPES = new Set(['wait_for_connection', 'check_connection', 'email_finder', /* 'check_stripe_customer', */ 'check_google_calendar_meeting']);
const FOUR_BRANCH_ARMS = [
  { key: 'repliedBranch', label: 'Replied', prefix: 'replied', statKey: 'replied', labelProps: { $color: '#16A34A', $bg: '#F0FDF4', $border: '#BBF7D0' } },
  { key: 'clickedBranch', label: 'Clicked', prefix: 'clicked', statKey: 'clicked', labelProps: { $color: '#2563EB', $bg: '#EFF6FF', $border: '#BFDBFE' } },
  { key: 'openedBranch', label: 'Opened', prefix: 'opened', statKey: 'opened', labelProps: { $color: '#7C3AED', $bg: '#F5F3FF', $border: '#DDD6FE' } },
  { key: 'nothingBranch', label: 'Nothing', prefix: 'nothing', statKey: 'nothing', labelProps: {} },
];

const FlowBuilderCanvas = ({
  canvasRef,
  isPanning,
  handleCanvasMouseDown,
  handleCanvasClick,
  pan,
  zoom,
  leadList,
  setLeadsModalOpen,
  activeAddButtonId,
  handleAddButtonClick,
  nodes,
  actionNodes,
  selectedNodeId,
  onNodeSelect,
  getNodeStats,
  analyticsData,
  completedExecutions,
  failedExecutions,
  runningExecutions,
  onStatClick,
  zoomOut,
  zoomIn,
  zoomPercent,
}) => {
  const renderNode = node => {
    const config = actionNodes.find(a => a.type === node.type);
    if (!config) return null;
    const isTwoBranching = BRANCHING_TYPES.has(node.type);
    const isFourBranching = node.type === 'check_email_status';
    return (
      <React.Fragment key={node.id}>
        <NodeRow>
          <FlowNodeCard
            node={node}
            config={config}
            selected={selectedNodeId === node.id}
            onSelect={() => onNodeSelect(node.id)}
            getNodeStats={getNodeStats}
            onStatClick={onStatClick}
          />
          <NodeAnalyticsWidget
            nodeType={node.type}
            analytics={analyticsData?.[node.id]}
            nodeStats={getNodeStats(node.id)}
          />
        </NodeRow>

        {isTwoBranching && (
          <BranchContainer data-interactive>
            <BranchVerticalLine $height="70px" />
            <BranchSplit>
              {renderBranchArm(node, 'yesBranch')}
              {renderBranchArm(node, 'noBranch')}
            </BranchSplit>
          </BranchContainer>
        )}

        {isFourBranching && (
          <BranchContainer data-interactive>
            <BranchVerticalLine $height="70px" />
            <BranchSplitFour>
              {FOUR_BRANCH_ARMS.map(arm => renderFourBranchArm(node, arm))}
            </BranchSplitFour>
          </BranchContainer>
        )}
      </React.Fragment>
    );
  };

  const renderBranchArm = (parentNode, branchKey) => {
    const branchList = parentNode.data?.[branchKey] || [];
    const isYes = branchKey === 'yesBranch';
    const branchPrefix = isYes ? 'yes' : 'no';
    const titleSuffix = isYes ? '(Yes)' : '(No)';

    return (
      <BranchArm>
        <BranchVerticalLine $height="16px" />
        <BranchLabel $yes={isYes}>{isYes ? 'Yes' : 'No'}</BranchLabel>
        <BranchVerticalLine $height="16px" />

        {branchList.length > 0 && (
          <>
            <AddNodeButton
              $active={activeAddButtonId === `${branchPrefix}-${parentNode.id}-pre-0`}
              title={`Add action ${titleSuffix}`}
              onClick={() => handleAddButtonClick(`${branchPrefix}-${parentNode.id}-pre-0`)}
            >
              <GoPlus color="var(--white)" />
            </AddNodeButton>
            <BranchVerticalLine $height="32px" />
          </>
        )}

        {branchList.map((branchNode, bIdx) => {
          const btnId = `${branchPrefix}-${parentNode.id}-at-${bIdx}`;
          return (
            <React.Fragment key={branchNode.id}>
              {renderNode(branchNode)}
              <BranchVerticalLine $height="32px" />
              <AddNodeButton
                $active={activeAddButtonId === btnId}
                title={`Add action ${titleSuffix}`}
                onClick={() => handleAddButtonClick(btnId)}
              >
                <GoPlus color="var(--white)" />
              </AddNodeButton>
              <BranchVerticalLine $height="32px" />
            </React.Fragment>
          );
        })}

        {branchList.length === 0 && (
          <AddNodeButton
            $active={activeAddButtonId === `${branchPrefix}-${parentNode.id}`}
            title={`Add action ${titleSuffix}`}
            onClick={() => handleAddButtonClick(`${branchPrefix}-${parentNode.id}`)}
          >
            <GoPlus color="var(--white)" />
          </AddNodeButton>
        )}

        <BranchVerticalLine $height="16px" />
      </BranchArm>
    );
  };

  const renderFourBranchArm = (parentNode, { key, label, prefix, statKey, labelProps }) => {
    const branchList = parentNode.data?.[key] || [];

    const emailStats = analyticsData?.[parentNode.id]?.stats || analyticsData?.[parentNode.id] || {};
    const hasAnyStats = ['total', 'replied', 'clicked', 'opened', 'nothing'].some(
      k => typeof emailStats?.[k] === 'number',
    );
    const branchCount = typeof emailStats?.[statKey] === 'number' ? emailStats[statKey] : 0;

    return (
      <BranchArmFour key={key} $hasNodes={branchList.length > 0}>
        <BranchVerticalLine $height="16px" />
        <BranchLabel {...labelProps}>
          <span>{label}</span>
          {hasAnyStats && <span className="branch-count">{branchCount}</span>}
        </BranchLabel>
        <BranchVerticalLine $height="16px" />

        {branchList.length > 0 && (
          <>
            <AddNodeButton
              $active={activeAddButtonId === `${prefix}-${parentNode.id}-pre-0`}
              title={`Add action (${label})`}
              onClick={() => handleAddButtonClick(`${prefix}-${parentNode.id}-pre-0`)}
            >
              <GoPlus color="var(--white)" />
            </AddNodeButton>
            <BranchVerticalLine $height="32px" />
          </>
        )}

        {branchList.map((branchNode, bIdx) => {
          const btnId = `${prefix}-${parentNode.id}-at-${bIdx}`;
          return (
            <React.Fragment key={branchNode.id}>
              {renderNode(branchNode)}
              <BranchVerticalLine $height="32px" />
              <AddNodeButton
                $active={activeAddButtonId === btnId}
                title={`Add action (${label})`}
                onClick={() => handleAddButtonClick(btnId)}
              >
                <GoPlus color="var(--white)" />
              </AddNodeButton>
              <BranchVerticalLine $height="32px" />
            </React.Fragment>
          );
        })}

        {branchList.length === 0 && (
          <AddNodeButton
            $active={activeAddButtonId === `${prefix}-${parentNode.id}`}
            title={`Add action (${label})`}
            onClick={() => handleAddButtonClick(`${prefix}-${parentNode.id}`)}
          >
            <GoPlus color="var(--white)" />
          </AddNodeButton>
        )}

        <BranchVerticalLine $height="16px" />
      </BranchArmFour>
    );
  };

  return (
    <CanvasArea
      ref={canvasRef}
      $isPanning={isPanning}
      onMouseDown={handleCanvasMouseDown}
      onClick={handleCanvasClick}
    >
      <CanvasTransformContent $panX={pan.x} $panY={pan.y} $zoom={zoom}>
        <StartNode>
          <SlFlag />
          Automation Start
        </StartNode>

        <ConnectorLine />

        <UploadNodeCard data-interactive>
          <FlowNodeHeader>
            <FlowNodeIcon $bgColor="rgba(59, 130, 246, 0.1)" $color="#3B82F6">
              <LuUsers />
            </FlowNodeIcon>
            <FlowNodeTitle>Upload Contacts</FlowNodeTitle>
          </FlowNodeHeader>
          <FlowNodeBody>
            <SelectDropdown onClick={() => setLeadsModalOpen(true)}>
              <LeadListText $selected={Boolean(leadList)}>{leadList || 'Select Leads'}</LeadListText>
              <IoIosArrowDown />
            </SelectDropdown>
          </FlowNodeBody>
        </UploadNodeCard>

        <ConnectorLineSimple $height="28px" />
        <AddNodeButton
          $active={activeAddButtonId === 'main'}
          title="Add action"
          onClick={() => handleAddButtonClick('main')}
        >
          <GoPlus color="var(--white)" />
        </AddNodeButton>
        <ConnectorLine $height="28px" />

        {nodes.map(node => (
          <React.Fragment key={node.id}>
            {renderNode(node)}
            <ConnectorLineSimple $height="28px" />
            <AddNodeButton
              $active={activeAddButtonId === `after-${node.id}`}
              title="Add action"
              onClick={() => handleAddButtonClick(`after-${node.id}`)}
            >
              <GoPlus color="var(--white)" />
            </AddNodeButton>
            <ConnectorLine $height="28px" />
          </React.Fragment>
        ))}

        {nodes.length > 0 && (
          <EndNodeCard>
            <EndNodeHeader>
              <span className="indicator" />
              <span>End Automation</span>
            </EndNodeHeader>
            <EndNodeBody>
              <StatusTable>
                <thead>
                  <tr>
                    <th>Task Status</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr data-clickable="true" onClick={onStatClick ? () => onStatClick({ nodeId: null, nodeLabel: null, status: 'completed' }) : undefined}>
                    <td className="status-completed">Completed</td>
                    <td className="count">{completedExecutions}</td>
                  </tr>
                  <tr data-clickable="true" onClick={onStatClick ? () => onStatClick({ nodeId: null, nodeLabel: null, status: 'failed' }) : undefined}>
                    <td className="status-failed">Failed</td>
                    <td className="count">{failedExecutions}</td>
                  </tr>
                  <tr data-clickable="true" onClick={onStatClick ? () => onStatClick({ nodeId: null, nodeLabel: null, status: 'running' }) : undefined}>
                    <td className="status-progress">In Progress</td>
                    <td className="count">{runningExecutions}</td>
                  </tr>
                </tbody>
              </StatusTable>
            </EndNodeBody>
          </EndNodeCard>
        )}
      </CanvasTransformContent>

      <ZoomPanel>
        <ZoomButton onClick={zoomOut} title="Zoom out">
          <AiOutlineZoomOut />
        </ZoomButton>
        <ZoomTrack>
          <ZoomIndicatorDynamic $left={zoomPercent} />
        </ZoomTrack>
        <ZoomButton onClick={zoomIn} title="Zoom in">
          <AiOutlineZoomIn />
        </ZoomButton>
      </ZoomPanel>
    </CanvasArea>
  );
};

export default FlowBuilderCanvas;
